/**
 * m3 echo Frontend - SSO Callback Page
 *
 * Handles the redirect back from the SSO provider.
 * Restores the session from the HttpOnly cookie set by the backend
 * during the SSO callback, then navigates to the dashboard.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { authService } from '../services/auth';
import { useAuthStore } from '../stores/useAuthStore';

export const SSOCallback: React.FC = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const response = await authService.checkSession();
        if (response) {
          setAuth(response);
          navigate('/', { replace: true });
        } else {
          setError('Sitzung konnte nicht wiederhergestellt werden. Bitte erneut anmelden.');
        }
      } catch {
        setError('Fehler bei der SSO-Anmeldung. Bitte erneut versuchen.');
      }
    };

    restoreSession();
  }, [setAuth, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        px: 2,
      }}
    >
      {error ? (
        <>
          <Alert severity="error" sx={{ maxWidth: 450, width: '100%' }}>
            {error}
          </Alert>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
          >
            Zur Anmeldeseite
          </Button>
        </>
      ) : (
        <>
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Anmeldung wird abgeschlossen...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default SSOCallback;
